/**
 * /payroll/year-end/940/:year — Annual Form 940 + Schedule A.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Alert, Box, Button, Card, CardContent, IconButton, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { api, getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/year-end_/940/$year")({
  component: Form940Page,
});

interface Deposit {
  id: string; year: number; quarter: number | null; deposit_date: string;
  amount: string; payment_method: string; eftps_confirmation: string | null;
}

const fmtMoney = (s: string | number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(s));

function Form940Page() {
  const { year } = Route.useParams();
  const yr = Number(year);

  const { data: deposits = [] } = useQuery({
    queryKey: ["payroll", "tax-deposits", yr, "940"],
    queryFn: async () =>
      (await api(`/api/payroll/year-end/deposits?year=${yr}&form_kind=940`)) as Deposit[],
  });

  const totalDeposited = deposits.reduce((sum, d) => sum + Number(d.amount), 0);

  const downloadPdf = (kind: "main" | "schedule_a") => {
    const url =
      kind === "main"
        ? `/api/payroll/year-end/940/${yr}.pdf`
        : `/api/payroll/year-end/940/${yr}/schedule-a.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = kind === "main" ? `Form-940-${yr}.pdf` : `Form-940-${yr}-Schedule-A.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton component={Link} to="/payroll/year-end" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">Back to Year-End</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Form 940 — {yr}</Typography>
          <Typography variant="body2" color="text.secondary">
            Annual Federal Unemployment Tax (FUTA). Effective rate is 0.6% on the first $7,000 per employee.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-year-end/filing-940.md" label="Form 940 + Schedule A guide" />
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => downloadPdf("main")}>
            Form 940 PDF
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => downloadPdf("schedule_a")}>
            Schedule A PDF
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={1}>FUTA Deposits ({yr})</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            FUTA deposits flow to <strong>Form 940 line 13</strong>. Add deposits via the{" "}
            <Link to="/payroll/tax-deposits">Tax Deposits</Link> page (form_kind = 940).
          </Typography>
          <Stack direction="row" spacing={3} mb={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total deposited</Typography>
              <Typography variant="h5" fontWeight={600}>{fmtMoney(totalDeposited)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Number of deposits</Typography>
              <Typography variant="h5" fontWeight={600}>{deposits.length}</Typography>
            </Box>
          </Stack>
          {deposits.length > 0 ? (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Quarter</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>EFTPS Confirmation</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deposits.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.deposit_date}</TableCell>
                      <TableCell>{d.quarter ? `Q${d.quarter}` : "—"}</TableCell>
                      <TableCell>{d.payment_method}</TableCell>
                      <TableCell>{d.eftps_confirmation || "—"}</TableCell>
                      <TableCell align="right">{fmtMoney(d.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Alert severity="info">
              No FUTA deposits recorded.{" "}
              <Link to="/payroll/tax-deposits">Add a deposit</Link>.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Alert severity="info">
        <strong>Schedule A:</strong> Required if you paid wages in more than one state, or any
        credit-reduction state. Set per-state <code>credit_reduction_rate</code> in
        Settings → Entity → state_tax_registrations to populate Schedule A correctly.
      </Alert>
    </Box>
  );
}
